import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-10 flex items-center gap-2">
            <img src="/logo-full.png" alt="CanHelp Move" className="h-8 w-auto" />
          </Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="hidden lg:flex relative bg-slate-950 text-white items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-dotted opacity-[0.08]" />
        <div className="relative max-w-md px-12">
          <div className="text-sm font-medium text-slate-400">AI relocation intelligence</div>
          <p className="mt-4 font-heading text-3xl font-semibold leading-snug tracking-tight">
            Find the city that fits the life you actually want to live.
          </p>
          <p className="mt-4 text-slate-400 text-sm leading-relaxed">
            Answer a few questions about your priorities — climate, cost, safety, lifestyle — and let CanHelp Move surface your best-matched destinations worldwide.
          </p>
          <div className="mt-10 flex items-center gap-6 text-slate-500 text-xs">
            <span>12+ global cities</span><span>·</span><span>17-point match model</span><span>·</span><span>Instant results</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your relocation journey.">
      <Button data-testid="google-login-btn" variant="outline" className="w-full h-11 gap-2 font-medium" onClick={googleLogin}>
        <GoogleIcon /> Continue with Google
      </Button>
      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="h-11" />
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 font-medium">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        Don't have an account?{" "}
        <Link to="/register" data-testid="goto-register" className="font-medium text-slate-900 underline underline-offset-4">Sign up</Link>
      </p>
    </AuthShell>
  );
}
